'use client';

import Image from 'next/image';
import { Carousel, Container } from 'react-bootstrap';
import type { PortableTextBlock } from 'next-sanity';

import Button from '@/componentv2/button/button';
import { LandingPortableText } from '@/components/landingPortableText/landingPortableText';
import { urlForImage } from '@/sanity/lib/utils';
import type { LANDING_OR_PRODUCT_PAGE_QUERY_RESULT } from '@/sanity.types';
import { getSectionBackgroundStyle } from '../../utils';
import './SolutionSection.scss';

type LandingPageData = Extract<
  NonNullable<LANDING_OR_PRODUCT_PAGE_QUERY_RESULT>,
  { _type: 'landingPage' }
>;

interface SolutionSectionProps {
  data: NonNullable<LandingPageData['solutionSection']>;
}

export default function SolutionSection({ data }: SolutionSectionProps) {
  const {
    sectionLabel,
    headline,
    bodyCopy,
    featureList,
    ctaText,
    ctaLink,
    productImages,
    background,
  } = data;

  return (
    <section
      className="rlp-solution-section"
      style={getSectionBackgroundStyle(background)}
    >
      <Container>
        {sectionLabel && (
          <span className="rlp-section-label">{sectionLabel}</span>
        )}
        <div className="rlp-solution-content">
          <div className="rlp-solution-visual">
            {headline && (
              <div className="rlp-section-headline">
                <LandingPortableText value={headline as PortableTextBlock[]} />
              </div>
            )}
            {productImages && productImages.length > 0 && (
              <div className="rlp-solution-images">
                <Carousel
                  className="rlp-carousel"
                  indicators={productImages.length > 1}
                  controls={false}
                  interval={4000}
                  pause="hover"
                >
                  {productImages.map((item) => (
                    <Carousel.Item key={item._key}>
                      <div className="rlp-solution-image">
                        <Image
                          src={
                            item.image
                              ? (urlForImage(item.image)
                                  ?.width(950)
                                  .height(950)
                                  .fit('crop')
                                  .url() as string)
                              : ''
                          }
                          alt={item.alt || ''}
                          width={475}
                          height={475}
                          sizes="475px"
                        />
                      </div>
                    </Carousel.Item>
                  ))}
                </Carousel>
              </div>
            )}
          </div>

          <div className="rlp-solution-details">
            {bodyCopy && (
              <div className="rlp-solution-body">
                <LandingPortableText value={bodyCopy as PortableTextBlock[]} />
              </div>
            )}
            {featureList && featureList.length > 0 && (
              <ul className="rlp-solution-features">
                {featureList.map((feature) => (
                  <li key={feature._key}>
                    {feature.title && <strong>{feature.title}</strong>}
                    {feature.description && (
                      <LandingPortableText
                        value={feature.description as PortableTextBlock[]}
                      />
                    )}
                  </li>
                ))}
              </ul>
            )}
            {ctaText && (
              <Button href={ctaLink} variant="primary">
                {ctaText}
              </Button>
            )}
          </div>
        </div>
      </Container>
    </section>
  );
}
